import {CommonModule} from '@angular/common';
import {Component, Inject, Input} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ArchivedHealthAnswers,
  bmi as computeBmi,
  CategoryCode,
  CurrentlyWorking,
  Drug,
  ENVIRONMENT,
  EnvironmentBroker,
  FamilialMedicalHistory,
  HealthAnswers,
  POUNDS_PER_KILOGRAM,
  TranslatedUiQuestion,
  WeightUnit,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DividerModule} from 'primeng/divider';
import {debounceTime} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {HealthQuestionnaire} from '../../../entities/proposal-page-object/health-questionnaire';
import {ArchivedHealthQuestionnaire} from '../../../entities/underwriting-page-object/underwriting-page-object';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {QuestionComponent} from '../../question/question.component';

interface HealthQuestionnaireForm {
  [key: string]: AbstractControl<unknown, unknown>;
}

@UntilDestroy()
@Component({
  selector: 'em-health-questionnaire-section',
  templateUrl: './health-questionnaire-section.component.html',
  imports: [CommonModule, ReactiveFormsModule, CardModule, ButtonModule, DividerModule, QuestionComponent],
})
export class HealthQuestionnaireSectionComponent extends BaseSectionComponent<
  FormGroup<HealthQuestionnaireForm>,
  {
    healthQuestionnaire?: HealthQuestionnaire | ArchivedHealthQuestionnaire;
    healthAnswers?: HealthAnswers | ArchivedHealthAnswers;
  },
  HealthAnswers
> {
  questions: TranslatedUiQuestion[] = [];
  bmi: number | undefined;
  isFFEnabled = false;

  readonly WEIGHT_UNIT_FORM_CONTROL_NAME = 'weightUnit';
  readonly CATEGORY_CODE = CategoryCode;

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT)
    environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  @Input({required: true})
  override set props(
    props:
      | {
          healthQuestionnaire?: HealthQuestionnaire | ArchivedHealthQuestionnaire;
          healthAnswers?: HealthAnswers | ArchivedHealthAnswers;
        }
      | undefined,
  ) {
    if (props && props.healthQuestionnaire && props.healthAnswers) {
      const healthAnswers = props.healthAnswers;
      const healthQuestionnaire = props.healthQuestionnaire;
      this.setBmi(healthAnswers);

      /*
      If the new questionnaire's questions are different from what the component has, we simply re-render the whole
      questionnaire (in place updates are difficult in this case). Otherwise, we check every question individually
      and replace them in case of a change (to the text, subtext, ineligibility trigger or EQ trigger). Doing so
      prevents re-rendering questions that stayed the same.
       */
      if (
        this.questions.length === 0 ||
        this.questions.some((q, i) => q.id !== healthQuestionnaire.questions[i].id)
      ) {
        this.createOrUpdateFormControls(healthQuestionnaire.questions, healthAnswers);
        this.questions = healthQuestionnaire.questions;
      } else {
        healthQuestionnaire.questions.forEach((q, i) => {
          const oldQuestion = this.questions[i];
          if (
            oldQuestion.text !== q.text ||
            JSON.stringify(oldQuestion.subtexts) !== JSON.stringify(q.subtexts) ||
            oldQuestion.causesIneligibility !== q.causesIneligibility ||
            oldQuestion.extraQuestionnaire !== q.extraQuestionnaire
          ) {
            this.questions[i] = q;
            const answerValue = typeof q.answer.value === 'object' ? q.answer.value['value'] : q.answer.value;
            this.form.get(q.answer.propertyName)!.patchValue(answerValue, {emitEvent: false});
          }
        });
      }
    }
  }

  getFormControl(controlName: string): FormControl {
    return this.form.get(controlName) as FormControl;
  }

  simulate(isEligible: boolean, withEQ = false): void {
    this.form.patchValue({
      citizenship: !isEligible ? Math.random() < 0.5 : true,
      height: Math.random() < 0.5 ? 177.8 : 182.88,
      weight: 60,
      weightUnit: WeightUnit.KILOGRAMS,
      pregnancy: false,
      weightLoss: withEQ ? Math.random() < 0.1 : false,
      smoker: Math.random() < 0.1,
      marijuana: withEQ ? Math.random() < 0.1 : false,
      currentlyWorking: withEQ
        ? Math.random() < 0.1
          ? CurrentlyWorking.STUDENT_OR_HOMEMAKER
          : CurrentlyWorking.PARENTAL_LEAVE
        : CurrentlyWorking.YES,
      occupationalDuties: withEQ ? Math.random() < 0.1 : false,
      criminalOffence: !isEligible ? Math.random() < 0.5 : false,
      familialMedicalHistoryUnder65: withEQ ? Math.random() < 0.1 : false,
      familialMedicalHistoryUnder60: withEQ
        ? Math.random() < 0.1
          ? FamilialMedicalHistory.YES_TWO_OR_MORE
          : FamilialMedicalHistory.NO_ONLY_ONE
        : FamilialMedicalHistory.NO,
      discomfortSymptoms: withEQ ? Math.random() < 0.1 : false,
      medicalInvestigations: withEQ ? Math.random() < 0.1 : false,
      extremeActivities: withEQ ? Math.random() < 0.1 : false,
      heartDisorders: withEQ ? Math.random() < 0.1 : false,
      diabetesDiagnosis: withEQ ? Math.random() < 0.1 : false,
      endocrineDisorders: withEQ ? Math.random() < 0.1 : false,
      cancerOrOthers: withEQ ? Math.random() < 0.1 : false,
      respiratoryDisorders: withEQ ? Math.random() < 0.1 : false,
      sleepApnea: withEQ ? Math.random() < 0.1 : false,
      gastrointestinalDisorders: !isEligible ? Math.random() < 0.5 : false,
      mildNervousDisorders: withEQ ? Math.random() < 0.1 : false,
      mildNervousDisordersTimeLoss: withEQ ? Math.random() < 0.1 : false,
      mildNervousDisordersHospitalization: withEQ ? Math.random() < 0.1 : false,
      mildNervousDisordersMedication: withEQ ? Math.random() < 0.1 : false,
      nervousDisorders: withEQ ? Math.random() < 0.1 : false,
      muscularDisorders: withEQ ? Math.random() < 0.1 : false,
      multipleSclerosis: withEQ ? Math.random() < 0.1 : false,
      neuralDisorders: withEQ ? Math.random() < 0.1 : false,
      epilepsyDisorders: false,
      genitourinaryDisorders: withEQ ? Math.random() < 0.1 : false,
      immuneDeficiencyDisorders: !isEligible ? Math.random() < 0.5 : false,
      chronicPainDisorders: withEQ ? Math.random() < 0.1 : false,
      drugUse: withEQ ? (Math.random() < 0.1 ? Drug.MORE_THAN_TWO_YEARS_AGO : Drug.NO) : Drug.NO,
      drugTreatment: withEQ ? (Math.random() < 0.1 ? Drug.MORE_THAN_TWO_YEARS_AGO : Drug.NO) : Drug.NO,
      specialTravel: withEQ ? Math.random() < 0.1 : false,
    });
  }

  private createForm(): FormGroup<HealthQuestionnaireForm> {
    const form = this.fb.group({});

    form.valueChanges
      .pipe(distinctUntilChanged(), debounceTime(DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((_) => {
        this.propsChange.emit({
          output: {
            ...this.formToOutput(),
            weight: this.getFormControl('weight').value
              ? {
                  value: this.getFormControl('weight').value,
                  unit: this.getFormControl(this.WEIGHT_UNIT_FORM_CONTROL_NAME).value,
                }
              : undefined,
          },
        });
      });

    return form;
  }

  private createOrUpdateFormControls(questions: TranslatedUiQuestion[], answers: HealthAnswers): void {
    questions.forEach((q) => {
      const answerValue = typeof q.answer.value === 'object' ? q.answer.value['value'] : q.answer.value;

      if (this.form.get(q.answer.propertyName)) {
        this.form.get(q.answer.propertyName)!.patchValue(answerValue, {emitEvent: false});
      } else {
        const control = this.fb.control(answerValue, [Validators.required]);
        this.form.addControl(q.answer.propertyName, control, {emitEvent: false});
      }
    });

    const weightUnit = answers.weight?.unit;
    this.form.addControl(
      this.WEIGHT_UNIT_FORM_CONTROL_NAME,
      this.fb.control(weightUnit ?? WeightUnit.POUNDS, [Validators.required]),
      {emitEvent: false},
    );
  }

  private setBmi(healthAnswers: HealthAnswers) {
    this.bmi = undefined;
    if (healthAnswers.weight && healthAnswers.height) {
      const weightInKg =
        healthAnswers.weight.unit === WeightUnit.KILOGRAMS
          ? healthAnswers.weight.value
          : healthAnswers.weight.value / POUNDS_PER_KILOGRAM;
      this.bmi = computeBmi(healthAnswers.height, weightInKg);
    }
  }
}
